import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { AppShell, DropdownMenu, IconButton, Navigation, NavigationGroup, NavigationItem, WorkspaceBadge } from '@humanforce/mango-ui-react';
import { HOME_PATH, itemPath, navSections } from '../../navigation/navConfig';

interface MainLayoutProps {
  children: ReactNode;
}

export const MainLayout = ({ children }: MainLayoutProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;

  const isHomeActive = path === HOME_PATH;
  const isItemActive = (target: string) => path === target || path.startsWith(`${target}/`);

  const sidebar = (
    <Navigation tone="blue">
      {navSections.map((section) => {
        // Home is the only direct destination; every other section is a non-navigating group.
        if (section.home)
          return (
            <NavigationItem key="home" prefix={section.icon} active={isHomeActive} onClick={() => navigate(HOME_PATH)}>
              {section.label}
            </NavigationItem>
          );
        return (
          <NavigationGroup key={section.slug} label={section.label} icon={section.icon} open={section.items?.some((item) => isItemActive(itemPath(section, item)))}>
            {section.items?.map((item) => (
              <NavigationItem key={item.slug} active={isItemActive(itemPath(section, item))} onClick={() => navigate(itemPath(section, item))}>
                {item.label}
              </NavigationItem>
            ))}
          </NavigationGroup>
        );
      })}
    </Navigation>
  );

  return (
    <AppShell
      fillViewport
      logo="/humanforce-yellow.png"
      logoShort="/hf-payroll.svg"
      logoAlt="Humanforce Payroll"
      topLeft={<WorkspaceBadge label="Humanforce Payroll" />}
      topRight={
        <>
          <IconButton icon="notifications" label="Notifications" variant="tertiary" size="small" />
          <IconButton icon="help" label="Help" variant="tertiary" size="small" />
          <DropdownMenu
            trigger={<IconButton icon="person" label="My account" variant="tertiary" size="small" />}
            items={[
              { label: 'My profile', icon: 'person' },
              { label: 'Settings', icon: 'settings' },
              { separator: true },
              { label: 'Sign out', icon: 'logout', variant: 'destructive' },
            ]}
          />
        </>
      }
      sidebar={sidebar}
    >
      <main className="mx-auto w-full max-w-7xl px-4 py-4 lg:px-6 lg:py-6">{children}</main>
    </AppShell>
  );
};
